<template>
  <div
    class="searchpanel fixed mw-100 bg-cl-primary cl-accent"
    data-testid="searchPanel"
  >
    <div class="close-icon-row">
      <i
        class="material-icons pointer cl-accent close-icon"
        @click="closeSearchpanel"
        data-testid="closeSearchPanel"
      >
        close
      </i>
    </div>
    <div class="container">
      <search-panel-configurator v-for="product in visibleProducts"
                    v-if="product.custom_options && product.custom_options.length > 0 && product.custom_options.filter(option => option.is_active === 1).length"
                    :product="product"
                    :key="product.sku"
      />
      <div class="row">
        <div class="col-md-12 end-xs">
          <label for="search" class="visually-hidden">
            {{ $t('Search') }}
          </label>
          <div class="search-input-group">
            <i class="material-icons search-icon">search</i>
            <input ref="search"
                   id="search"
                   :value="search"
                   @input="search = $event.target.value"
                   @blur="$v.search.$touch()"
                   class="search-panel-input"
                   :placeholder="$t('Type what you are looking for...')"
                   type="search"
                   autofocus="true"
                   data-hj-whitelist
                   autocomplete="off"
            >
            <img src="../../../../assets/icons/close-field.png"
                 class="search-clean-all"
                 width="14"
                 height="14"
                 alt="Close"
                 :class="{ activated: isCleanBtnActive }"
                 @click="cleanAllProducts"
                 loading="eager"
                 fetchpriority="high"
            >
          </div>
        </div>
      </div>
      <div class="product-listing row">
        <product-tile
          v-for="product in visibleProducts"
          :key="product.entity_id"
          :product="product"
          class="product-tile"
        />
        <transition name="fade">
          <div
            v-if="getNoResultsMessage && search.length > 2"
            class="no-results relative center-xs h4 col-md-12"
          >
            {{ $t(getNoResultsMessage) }}
          </div>
        </transition>
      </div>
      <div
        v-show="OnlineOnly && visibleProducts.length >= 18"
        class="buttons-set align-center py35 mt20 px40"
        :class="{ modify: getNoResultsMessage }"
      >
        <button
          @click="seeMore" v-if="readMore"
          class="no-outline brdr-none py15 px20 bg-cl-mine-shaft :bg-cl-th-secondary cl-white fs-medium-small"
          type="button"
        >
          {{ $t('Load more') }}
        </button>
        <button
          @click="closeSearchpanel"
          class="no-outline brdr-none p15 fs-medium-small close-button"
          type="button"
        >
          {{ $t('Close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import SearchPanel from '@vue-storefront/core/compatibility/components/blocks/SearchPanel/SearchPanel'
import ProductTile from 'theme/components/core/ProductTile'
import VueOfflineMixin from 'vue-offline/mixin'
import CategoryPanel from 'theme/components/core/blocks/Category/CategoryPanel'
import { minLength } from 'vuelidate/lib/validators'
import Configurator from 'theme/components/theme/blocks/Home/ProductTileGrid/Configurator'
import SearchPanelConfigurator from "./SearchPanelConfigurator";

export default {
  components: {
    ProductTile,
    CategoryPanel,
    Configurator,
    SearchPanelConfigurator
  },
  mixins: [SearchPanel, VueOfflineMixin],
  validations: {
    search: {
      minLength: minLength(3)
    }
  },
  data () {
    return {
      debounceSearch: () => {},
      selectedCategoryIds: [],
      isCleanBtnActive: false
    }
  },
  created () {
    this.debounceSearch = debounce(this.makeSearch, 500)
  },
  computed: {
    visibleProducts () {
      const productList = this.products || []
      if (this.selectedCategoryIds.length) {
        return productList.filter(product => product.category_ids.some(categoryId => {
          const catId = parseInt(categoryId)
          return this.selectedCategoryIds.includes(catId)
        }))
      }
      return productList
    },
    categories () {
      const categories = this.products
        .filter(p => p.category)
        .map(p => p.category)
        .flat()
        .filter(c => c.name)

      const discinctCategories = Array.from(
        new Set(categories.map(c => c.category_id))
      ).map(catId => categories.find(c => c.category_id === catId))

      return discinctCategories
    },
    getNoResultsMessage () {
      let msg = ''
       if (this.emptyResults) {
        msg = 'No results were found.'
      }
      return msg
    }
  },
  watch: {
    categories () {
      this.selectedCategoryIds = []
    },
    search () {
      if (this.search) this.isCleanBtnActive = true;
      else this.isCleanBtnActive = false;
      this.debounceSearch()
    }
  },
  mounted () {
    // add autofocus to search input field
    this.$refs.search.focus()
  },
  methods: {
    cleanAllProducts () {
      this.search = '';
      this.products = [];
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/animations/transitions";
@import "~theme/css/variables/grid";
@import "~theme/css/variables/typography";

.searchpanel {
  height: -webkit-fill-available;
  width: 100%;
  top: 0;
  right: 0;
  z-index: 3;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  .close-icon-row {
    display: flex;
    justify-content: flex-end;
  }

  .container {
    padding-left: 40px;
    padding-right: 40px;

    @media #{$media-xs} {
      padding-left: 30px;
      padding-right: 30px;
    }
  }
  .product-tile {
    box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.2);
    border: solid 1px #ffffff;
  }
  .row {
    margin-left: - map-get($grid-gutter-widths, lg) / 2;
    margin-right: - map-get($grid-gutter-widths, lg) / 2;

    @media #{$media-xs} {
      margin-right: - map-get($grid-gutter-widths, xs) / 2;
      margin-left: - map-get($grid-gutter-widths, xs) / 2;
    }
  }

  .col-md-12 {
    padding-left: map-get($grid-gutter-widths, lg) / 2;
    padding-right: map-get($grid-gutter-widths, lg) / 2;

    @media #{$media-xs} {
      padding-left: map-get($grid-gutter-widths, xs) / 2;
      padding-right: map-get($grid-gutter-widths, xs) / 2;
    }
  }

  .product-listing {
    padding-top: 30px;
  }

  .product {
    box-sizing: border-box;
    width: 22%;
    margin-right: 10px;
    margin-left: 10px;
    padding-left: map-get($grid-gutter-widths, lg) / 2;
    padding-right: map-get($grid-gutter-widths, lg) / 2;

    @media #{$media-xs} {
      width: 42%;
      padding-left: map-get($grid-gutter-widths, xs) / 2;
      padding-right: map-get($grid-gutter-widths, xs) / 2;
    }
  }

  .close-icon {
    padding: 18px 8px;
  }

  .search-input-group {
    display: flex;
    border-bottom: 1px solid #bdbdbd;
    position: relative;
  }

  .search-icon {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .search-panel-input {
    -webkit-appearance: none;
    background: none;
    width: calc(100% - 115px);
    height: 60px;

    padding-bottom: 0;
    padding-top: 0;

    font-size: 18px;
    font-family: map-get($font-families, secondary);

    border: none;
    outline: 0;

    &::-webkit-search-cancel-button {
      display: none;
    }

    @media #{$media-xs} {
      font-size: 16px;
    }
  }

  .search-clean-all {
    width: 14px;
    height: 14px;

    display: none;

    position: absolute;
    top: 0;
    right: 20px;
    bottom: 0;
    margin: auto;

    &.activated {
      display: flex;
    }

    &:hover {
      cursor: pointer;
    }
  }

  .no-results {
    top: 80px;
    width: 100%;
  }

  i {
    opacity: 0.6;
  }

  i:hover {
    opacity: 1;
  }

  .close-button {
    background: #fff;
  }

  button {
    @media #{$media-xs} {
      width: 100%;
      margin-bottom: 15px;
    }
  }
}
.modify {
  margin-top: 80px;
}

</style>
